import React, { useState, useEffect, useMemo, useCallback } from "react";
import MedicalCenterCard from "../components/medical-center-list/medicalCenterCard";
import MedicalCentersFilter from "../components/medical-center-list/medicalCentersFilter";
import {
  getCenters,
  getCities,
  getCategories,
  getGroups,
  getInsurances,
} from "../services/centerService";
import bottomArrow from "../assets/icons/bottom-arrow.png";

const MedicalCentersListPage = () => {
  // فیلترها (بر اساس فیلدهای مورد نیاز API)
  const [filters, setFilters] = useState({
    search: "",
    city_id: "",
    category_id: "",
    group_id: "",
    insurance_id: "",
  });

  const [sortBy, setSortBy] = useState("newest");
  const [currentPage, setCurrentPage] = useState(1);
  const [openDropdown, setOpenDropdown] = useState(null);

  // وضعیت‌های مربوط به لودینگ و ذخیره دیتای API مراکز درمانی
  const [centers, setCenters] = useState([]);
  const [totalCentersCount, setTotalCentersCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // وضعیت ذخیره لیست گزینه‌های دراپ‌دان که از API می‌آیند
  const [filterOptions, setFilterOptions] = useState({
    cities: [],
    categories: [],
    treatmentGroups: [],
    insurances: [],
  });
  const [loadingFilters, setLoadingFilters] = useState(false);

  const itemsPerPage = 20;

  // دریافت اطلاعات ۴ دراپ‌دان در ابتدای لود صفحه
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      setLoadingFilters(true);
      try {
        const [cities, categories, groups, insurances] = await Promise.all([
          getCities(),
          getCategories(),
          getGroups(),
          getInsurances(),
        ]);

        setFilterOptions({
          cities,
          categories,
          treatmentGroups: groups,
          insurances,
        });
      } catch (err) {
        console.error("خطا در دریافت اطلاعات دراپ‌دان‌ها:", err);
      } finally {
        setLoadingFilters(false);
      }
    };

    fetchDropdownOptions();
  }, []);

  // تابع دریافت اطلاعات مراکز درمانی از API
  const fetchCentersData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        search: filters.search,
        city_id: filters.city_id,
        category_id: filters.category_id,
        group_id: filters.group_id,
        insurance_id: filters.insurance_id,
        page: currentPage,
        limit: itemsPerPage,
      };

      const response = await getCenters(params);

      if (response) {
        if (response.response_status === "success") {
          // وضعیت عادی: دیتایی پیدا شده است
          setCenters(response.data || []);
          setTotalCentersCount(response.total || 0);
        } else if (
          response.response_status === "failed" ||
          response.response_status === "error" ||
          !response.data ||
          response.data.length === 0
        ) {
          setCenters([]);
          setTotalCentersCount(0);
        } else {
          setError("فرمت پاسخ ناپایدار است.");
        }
      } else {
        setError("پاسخی دریافت نشد.");
      }
    } catch (err) {
      console.error("خطای ارتباط با سرور:", err);
      setError("ارتباط برقرار نشد، لطفا دوباره تلاش کنید.");
    } finally {
      setLoading(false);
    }
  }, [filters, currentPage, itemsPerPage]);

  // اجرای درخواست با تغییر صفحه یا فیلترها
  useEffect(() => {
    fetchCentersData();
  }, [fetchCentersData]);

  // بازگرداندن صفحه به ۱ هنگام تغییر فیلترها
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  // محاسبه تعداد کل صفحات بر اساس دیتای سرور
  const totalPages = Math.ceil(totalCentersCount / itemsPerPage);

  // تابع مدیریت تغییر صفحه
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  // ریست کردن فیلترها
  const handleResetFilters = () => {
    setFilters({
      search: "",
      city_id: "",
      category_id: "",
      group_id: "",
      insurance_id: "",
    });
    setSortBy("newest");
    setCurrentPage(1);
    setOpenDropdown(null);
  };

  const isAnyFilterActive = Object.values(filters).some(
    (val) => String(val).trim() !== "",
  );

  // تبدیل دیتای خام API به فرمت مورد نیاز کارت کامپوننت شما (Data Mapping)
  const formattedCenters = useMemo(() => {
    let list = centers.map((item) => ({
      id: item.uuid,
      title: item.title,
      city: item.city_title,
      address: item.address,
      category: item.category_title,
      treatmentGroup: item.group_title,
      ownership: item.type, // دولتی یا خصوصی
      status: "فعال",
      isOpen: item.is_open,
      img: `https://bit.medizen.ir/i/${item.cover_photo}`,
      profile: `https://bit.medizen.ir/i/${item.thumbnail}`,
    }));

    if (sortBy === "mostVisited") {
      return [...list].sort((a, b) => b.views - a.views);
    }
    return list;
  }, [centers, sortBy]);

  useEffect(() => {
    // ۱. ذخیره کردن مقادیر اولیه برای بازگردانی در هنگام خروج از صفحه
    const originalHtmlOverflowX = document.documentElement.style.overflowX;
    const originalBodyOverflowX = document.body.style.overflowX;
    const originalHtmlOverflowY = document.documentElement.style.overflowY;
    const originalBodyOverflowY = document.body.style.overflowY;

    // ۲. اعمال استایل‌های جدید برای فعال شدن حالت Sticky در این صفحه
    document.documentElement.style.overflowX = "visible";
    document.body.style.overflowX = "visible";

    // اختیاری: اطمینان از اینکه سرریز عمودی روی visible است
    document.documentElement.style.overflowY = "visible";
    document.body.style.overflowY = "visible";

    // ۳. تابع Cleanup: بازگرداندن استایل‌ها به حالت اولیه وقتی کاربر صفحه را ترک می‌کند
    return () => {
      document.documentElement.style.overflowX = originalHtmlOverflowX;
      document.body.style.overflowX = originalBodyOverflowX;
      document.documentElement.style.overflowY = originalHtmlOverflowY;
      document.body.style.overflowY = originalBodyOverflowY;
    };
  }, []);

  return (
    <div
      className="flex w-full justify-center lg:-mt-4 pb-[40px] lg:pb-[70px]"
      onClick={() => setOpenDropdown(null)}
    >
      <div className="w-full py-2 md:w-[85%]">
        <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">
          {/* ستون راست: فیلترها */}
          <div className="lg:col-span-4 xl:col-span-3 lg:ml-3 lg:sticky lg:top-[80px] lg:self-start z-10 ">
            <MedicalCentersFilter
              filters={filters}
              setFilters={handleFilterChange}
              options={filterOptions}
              isLoadingFilters={loadingFilters} // در صورت نیاز برای نمایش حالت لودینگ در فیلترها
              isAnyFilterActive={isAnyFilterActive}
              onReset={handleResetFilters}
              openDropdown={openDropdown}
              setOpenDropdown={setOpenDropdown}
            />
          </div>

          {/* ستون چپ: لیست مراکز */}
          <div className="lg:col-span-8 xl:col-span-9">
            {/* بخش بالای لیست (عنوان و مرتب‌سازی) */}
            <div
              className="mb-3 lg:mb-5 flex flex-row items-center justify-between 
            rounded-2xl bg-white p-4 shadow-sm"
            >
              <div className="flex flex-col md:flex-row gap-1 md:gap-3 items-center">
                <h2 className="font-bold text-[17px] lg:text-[19px] text-black/80">
                  لیست مراکز درمانی
                </h2>
                <div className="text-[12px] lg:text-[13px] text-gray-500">
                  <span className="bg-gray-200 rounded-full h-3 w-3 px-1.5 py-0.5 font-medium">
                    {totalCentersCount}
                  </span>
                  {"  "}
                  مرکز درمانی یافت شد
                </div>
              </div>
              {/* مرتب سازی */}
              <div className="relative inline-flex items-center">
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-10 rounded-xl bg-gray-50 text-[13px] lg:text-sm 
                  text-gray-800 outline-none border border-gray-300
                   focus:border-[#49D1CD] px-3 pl-10 appearance-none"
                >
                  <option value="newest">جدیدترین</option>
                  <option value="mostVisited">پر بازدیدترین</option>
                </select>
                <img
                  src={bottomArrow}
                  alt=""
                  className="pointer-events-none absolute left-3 h-1.5 opacity-70"
                />
              </div>
            </div>

            {/* وضعیت لودینگ لیست اصلی */}
            {loading ? (
              <div className="flex h-64 items-center justify-center rounded-2xl bg-white p-4 text-center shadow-sm">
                <p className="text-gray-500 text-[15px]">
                  در حال دریافت اطلاعات مراکز درمانی...
                </p>
              </div>
            ) : error ? (
              /* وضعیت خطا */
              <div className="flex h-64 flex-col items-center justify-center rounded-2xl bg-white p-4 text-center shadow-sm">
                <p className="text-red-500 font-medium">{error}</p>
                <button
                  onClick={fetchCentersData}
                  className="mt-4 rounded-xl bg-[#49D1CD] px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  تلاش مجدد
                </button>
              </div>
            ) : formattedCenters.length > 0 ? (
              <>
                {/* نمایش کارت‌ها */}
                <div className="grid grid-cols-1 gap-3 md:gap-5 md:grid-cols-2 xl:grid-cols-2">
                  {formattedCenters.map((center) => (
                    <MedicalCenterCard key={center.id} center={center} />
                  ))}
                </div>

                {/* نمایش ناوبری پیجینیشن */}
                {totalPages > 1 && (
                  <div className="mt-8 flex justify-center">
                    <nav className="flex items-center space-x-2 space-x-reverse">
                      <button
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                      >
                        قبلی
                      </button>
                      {[...Array(totalPages).keys()].map((num) => (
                        <button
                          key={num + 1}
                          onClick={() => goToPage(num + 1)}
                          className={`h-9 w-9 rounded-lg text-sm ${
                            currentPage === num + 1
                              ? "bg-[#49D1CD] text-white"
                              : "bg-white text-gray-700 hover:bg-gray-100"
                          }`}
                        >
                          {num + 1}
                        </button>
                      ))}
                      <button
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50"
                      >
                        بعدی
                      </button>
                    </nav>
                  </div>
                )}
              </>
            ) : (
              /* پیام خالی بودن لیست */
              <div className="flex h-64 flex-col items-center justify-center rounded-2xl bg-white p-4 text-center shadow-sm">
                <h3 className="text-lg font-bold text-gray-800">
                  مرکز درمانی یافت نشد!
                </h3>
                <p className="mt-2 text-sm text-gray-500">
                  لطفاً فیلترهای خود را تغییر دهید یا صفحه را دوباره بارگذاری
                  کنید.
                </p>
                <button
                  onClick={handleResetFilters}
                  className="mt-4 rounded-xl bg-[#B00EB1] px-5 py-2.5 text-sm font-semibold text-white transition hover:opacity-90"
                >
                  پاک کردن فیلترها
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MedicalCentersListPage;
